import { AbstractControl, ValidationErrors, ValidatorFn } from "@angular/forms";

export function esFechaISO(fecha: string): boolean {
  return /^(\d{4})-(\d{2})-(\d{2})$/.test(fecha);
}

export function esFechaFormato(fecha: string): boolean {
  return /^(\d{2})\/(\d{2})\/(\d{4})$/.test(fecha);
}

export function esFechaString(fecha: string): boolean {
  return esFechaISO(fecha) || esFechaFormato(fecha);
}


/**
 * Transforma las fechas de un objeto del formato <yyyy-MM-dd> al formato <dd/MM/yyyy>.
 * @param obj Objeto con fechas en formato <yyyy-MM-dd>.
 * @returns Objeto con fechas en formato <dd/MM/yyyy>.
 */
export function transformarFechasISOAFormato<Data extends object>(obj: any): Data {
  const fechaFormatoRegex = /^(\d{4})-(\d{2})-(\d{2})$/;
  const resultado: any = Array.isArray(obj) ? [] : {};

  for (const key in obj) {
    if (!obj.hasOwnProperty(key)) continue;
    const valor = obj[key];
    if (typeof valor === 'string' && fechaFormatoRegex.test(valor)) {
      const match = valor.match(fechaFormatoRegex);
      if (match) {
        const [, anio, mes, dia] = match;
        resultado[key] = `${dia}/${mes}/${anio}`;
      } else {
        resultado[key] = valor;
      }
    } else if (typeof valor === 'object' && valor !== null) {
      resultado[key] = transformarFechasISOAFormato(valor);
    } else {
      resultado[key] = valor;
    }
  }
  return resultado as Data;
}

/**
 * Transforma las fechas de un objeto del formato <dd/MM/yyyy> al formato <yyyy-MM-dd>.
 * @param obj Objeto con fechas en formato <dd/MM/yyyy>.
 * @returns Objeto con fechas en formato <yyyy-MM-dd>.
 */
export function transformarFechasFormatoAISO<Data extends object>(obj: any): Data {
  const fechaFormatoRegex = /^\d{2}\/\d{2}\/\d{4}$/;
  const resultado: any = Array.isArray(obj) ? [] : {};

  for (const key in obj) {
    if (!obj.hasOwnProperty(key)) continue;
    const valor = obj[key];
    if (typeof valor === 'string' && fechaFormatoRegex.test(valor)) {
      const [dia, mes, anio] = valor.split('/');
      resultado[key] = `${anio}-${mes}-${dia}`;
    } else if (typeof valor === 'object' && valor !== null) {
      resultado[key] = transformarFechasFormatoAISO(valor);
    } else {
      resultado[key] = valor;
    }
  }
  return resultado as Data;
}

export function dateToISO(date: Date): string {
  return date.toISOString().split('T')[0];
}

export function dateToFormato(date: Date): string {
  return dateToISO(date).split('-').reverse().join('/');
}

export function hoyISO(): string {
  return dateToISO(new Date());
}

export function hoyFormato(): string {
  return dateToFormato(new Date());
}

/**
 * Transforma una fecha en formato yyyy-MM-dd a dd/MM/yyyy.
 * @param fechaISO Fecha en formato <yyyy-MM-dd>.
 * @returns Fecha en formato <dd/MM/yyyy>.
 */
export function isoAFormato(fechaISO: string): string {
  if (!fechaISO) return fechaISO;
  const fechaISORegex = /^(\d{4})-(\d{2})-(\d{2})/;
  const match = fechaISO.match(fechaISORegex);
  if (!match) return fechaISO;

  const [, anio, mes, dia] = match;
  return `${dia}/${mes}/${anio}`;
}

/**
 * Transforma una fecha en formato dd/MM/yyyy a yyyy-MM-dd.
 * @param fechaFormato Fecha en formato <dd/MM/yyyy>.
 * @returns Fecha en formato <yyyy-MM-dd>.
 */
export function formatoAISO(fechaFormato: string): string {
  if (!fechaFormato) return fechaFormato;
  const fechaNoISOregex = /^(\d{2})\/(\d{2})\/(\d{4})$/;
  const match = fechaFormato.match(fechaNoISOregex);
  if (!match) return fechaFormato;

  const [, dia, mes, anio] = match;
  return `${anio}-${mes}-${dia}`;
}

export type UnidadTiempo = 'dias' | 'meses' | 'años';

/**
 * Suma o resta unidades de tiempo a una fecha en formato dd/MM/yyyy, sin usar Date.
 * @param fechaStr Fecha en formato string.
 * @param cantidad Número de unidades a sumar (positivo) o restar (negativo).
 * @param unidad Unidad de tiempo: 'dias', 'meses', 'anios'.
 * @returns Nueva fecha en el mismo formato que la original.
 */
export function sumarRestarFechaString(fechaStr: string, cantidad: number, unidad: UnidadTiempo): string {
  const fechaFormatoRegex = /^(\d{2})\/(\d{2})\/(\d{4})$/;
  const match = fechaStr.match(fechaFormatoRegex);
  if (!match) return fechaStr;

  let [, d, m, y] = match;
  let dia = parseInt(d, 10);
  let mes = parseInt(m, 10);
  let anio = parseInt(y, 10);

  const esBisiesto = (a: number) => a % 4 === 0 && (a % 100 !== 0 || a % 400 === 0);
  const diasMes = (a: number, m: number) =>
    [31, esBisiesto(a) ? 29 : 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31][m - 1];

  switch (unidad) {
    case 'dias':
      dia += cantidad;
      while (dia > diasMes(anio, mes)) {
        dia -= diasMes(anio, mes);
        mes++;
        if (mes > 12) {
          mes = 1;
          anio++;
        }
      }
      while (dia < 1) {
        mes--;
        if (mes < 1) {
          mes = 12;
          anio--;
        }
        dia += diasMes(anio, mes);
      }
      break;
    case 'meses':
      mes += cantidad;
      while (mes > 12) {
        mes -= 12;
        anio++;
      }
      while (mes < 1) {
        mes += 12;
        anio--;
      }
      if (dia > diasMes(anio, mes)) dia = diasMes(anio, mes);
      break;
    case 'años':
      anio += cantidad;
      if (mes === 2 && dia > diasMes(anio, mes)) dia = diasMes(anio, mes);
      break;
  }

  const diaStr = String(dia).padStart(2, '0');
  const mesStr = String(mes).padStart(2, '0');
  const anioStr = String(anio);

  return `${diaStr}/${mesStr}/${anioStr}`;
}

export function normalizarFecha(fecha: string): string {
  if (/^\d{4}-\d{2}-\d{2}$/.test(fecha)) {
    return fecha; // Ya está en formato ISO
  }

  if (/^\d{2}\/\d{2}\/\d{4}$/.test(fecha)) {
    // Convertir dd/MM/yyyy a yyyy-MM-dd
    const [dia, mes, anio] = fecha.split('/');
    return `${anio}-${mes}-${dia}`;
  }

  return null; // si no es una fecha valida, devuelve null
};

/**
 * Compara dos fechas y devuelve true si la fecha1 es mayor que la fecha2.
 * @param fecha1 Fecha en formato dd/MM/yyyy o yyyy-MM-dd.
 * @param fecha2 Fecha en formato dd/MM/yyyy o yyyy-MM-dd.
 * @returns true si la fecha1 es mayor que la fecha2, false en caso contrario.
 */
export function fechaMayorQueFecha(fecha1: string, fecha2: string): boolean {
  if (!fecha1 || !fecha2) return false; // si no hay fecha, devuelve false

  // Normalizar fechas a formato ISO para comparación
  const fecha1Normalizada = normalizarFecha(fecha1);
  if (!fecha1Normalizada) return false; // si no es una fecha valida, devuelve false

  const fecha2Normalizada = normalizarFecha(fecha2);
  if (!fecha2Normalizada) return false; // si no es una fecha valida, devuelve false

  // Comparar como strings (funciona porque yyyy-MM-dd es comparable lexicográficamente)
  return fecha1Normalizada > fecha2Normalizada;
}

/**
 * Valida que la fecha de inicio sea menor que la fecha de fin en un FormGroup.
 * @param group FormGroup con las fechas de inicio y fin.
 * @returns ValidationErrors | null.
 */
export function rangoFechasValido(campoInicio: string, campoFin: string): ValidatorFn {
  return (group: AbstractControl): ValidationErrors | null => {
    const inicio = group.get(campoInicio)?.value;
    const fin = group.get(campoFin)?.value;
    if (fechaMayorQueFecha(inicio, fin)) {
      return { rangoFechasInvalido: true };
    }
    return null;
  };
}

export function tiempoEntreFechas(fechaInicio: string, fechaFin: string, unidad: UnidadTiempo): number {
  const fechaInicioDate = new Date(normalizarFecha(fechaInicio));
  const fechaFinDate = new Date(normalizarFecha(fechaFin));
  const tiempo = fechaFinDate.getTime() - fechaInicioDate.getTime();

  switch (unidad) {
    case 'dias':
      return tiempo / (1000 * 60 * 60 * 24);
    case 'meses':
      return tiempo / (1000 * 60 * 60 * 24 * 30);
    case 'años':
      return tiempo / (1000 * 60 * 60 * 24 * 365);
  }
}

export function fechaLargaEspanol(fecha: string): string {
  if (!fecha) return '';
  // Normalizar a formato ISO
  let iso = '';
  if (esFechaFormato(fecha)) {
    // dd/MM/yyyy
    const [dia, mes, anio] = fecha.split('/');
    iso = `${anio}-${mes}-${dia}`;
  } else if (esFechaISO(fecha)) {
    // yyyy-MM-dd
    iso = fecha;
  } else {
    return fecha; // formato no reconocido
  }

  const meses = [
    'enero', 'febrero', 'marzo', 'abril', 'mayo', 'junio',
    'julio', 'agosto', 'septiembre', 'octubre', 'noviembre', 'diciembre'
  ];

  const match = iso.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!match) return fecha;

  const [, anio, mes, dia] = match;
  const mesNombre = meses[parseInt(mes, 10) - 1];
  // Eliminar ceros a la izquierda del día
  const diaNum = parseInt(dia, 10);

  return `${diaNum} de ${mesNombre} del ${anio}`;
}
